import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Check,
  ChevronDown,
  Loader2,
  Search,
  User,
  Edit2,
  X,
  Info,
  Package,
} from "lucide-react";
import { toast } from "sonner";
import { toastError } from "@/lib/toast-error";
import { AddressInput, AddressValue, emptyAddress, addressToPayload } from "@/components/AddressInput";
import { useListCatalogProducts, getListCatalogProductsQueryKey } from "@workspace/api-client-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface SfSupplier {
  id: string;
  name: string;
  routingBasis?: string;
  channelManagers: Array<{ id: string; name: string; email: string; phone?: string; title?: string; supportedStates?: string }>;
}

interface Override {
  name: string;
  email: string;
  phone?: string;
  title?: string;
  notes?: string;
}

interface SupplierBundle {
  sfAccountId: string;
  accountName: string;
  routingBasis?: string;
  channelManagers: Array<{ id: string; name: string; email: string; phone?: string; title?: string; supportedStates?: string }>;
  hasOverride: boolean;
  override?: Override;
  error?: string;
}

const STEPS = ["Deal Info", "Select Suppliers", "Review CMs", "Submit"];

interface ProductMultiSelectProps {
  selected: string[];
  onChange: (selected: string[]) => void;
  options: string[];
  isLoading?: boolean;
  isEmpty?: boolean;
}

function ProductMultiSelect({ selected, onChange, options, isLoading, isEmpty }: ProductMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const filtered = options.filter((o) =>
    !search || o.toLowerCase().includes(search.toLowerCase()),
  );

  function toggle(name: string) {
    onChange(selected.includes(name) ? selected.filter((s) => s !== name) : [...selected, name]);
  }

  function removeSelected(name: string, e: React.MouseEvent) {
    e.stopPropagation();
    onChange(selected.filter((s) => s !== name));
  }

  if (isLoading) return <Skeleton className="h-10 w-full" />;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        className="w-full min-h-10 px-3 py-2 rounded-md border border-input bg-background text-sm flex items-start justify-between gap-2 hover:bg-accent/50 transition-colors"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="flex flex-wrap gap-1.5 flex-1 text-left">
          {selected.length === 0 ? (
            <span className="text-muted-foreground">
              {isEmpty ? "No products in catalog — add some in Settings" : "Select products…"}
            </span>
          ) : (
            selected.map((name) => (
              <Badge key={name} variant="secondary" className="gap-1 text-xs">
                {name}
                <span
                  role="button"
                  tabIndex={0}
                  className="ml-0.5 hover:text-destructive cursor-pointer"
                  onClick={(e) => removeSelected(name, e)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); removeSelected(name, e as unknown as React.MouseEvent); } }}
                >
                  <X className="h-3 w-3" />
                </span>
              </Badge>
            ))
          )}
        </span>
        <ChevronDown className={`h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && !isEmpty && (
        <div className="absolute z-50 w-full mt-1 rounded-md border bg-popover shadow-lg">
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <input
                className="w-full pl-8 pr-3 py-1.5 text-sm rounded-sm bg-background border-0 outline-none ring-1 ring-border focus:ring-primary"
                placeholder="Search products…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-52 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-3">No products match</p>
            ) : (
              filtered.map((name) => {
                const isSelected = selected.includes(name);
                return (
                  <button
                    key={name}
                    type="button"
                    className={`w-full text-left flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-accent transition-colors ${isSelected ? "text-foreground" : "text-foreground"}`}
                    onClick={() => toggle(name)}
                  >
                    <div className={`flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${isSelected ? "bg-primary border-primary" : "border-muted-foreground/40"}`}>
                      {isSelected && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                    </div>
                    {name}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center gap-2 mb-6">
      {STEPS.map((label, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className={`flex items-center justify-center rounded-full w-7 h-7 text-xs font-bold border-2 transition-colors ${i < current ? "bg-primary border-primary text-primary-foreground" : i === current ? "border-primary text-primary" : "border-muted-foreground/30 text-muted-foreground/40"}`}>
            {i < current ? <Check className="h-3.5 w-3.5" /> : i + 1}
          </div>
          <span className={`text-sm font-medium ${i === current ? "text-foreground" : "text-muted-foreground"}`}>{label}</span>
          {i < STEPS.length - 1 && <div className="w-8 h-px bg-border mx-1" />}
        </div>
      ))}
    </div>
  );
}

export default function NewLocalDeal() {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(0);

  const [form, setForm] = useState({
    accountName: "",
    opportunityName: "",
    ownerName: "",
    ownerEmail: "",
    primaryContactName: "",
    primaryContactEmail: "",
    primaryContactPhone: "",
    closeDate: "",
    mrr: "",
    tcv: "",
    notes: "",
  });
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [billingAddress, setBillingAddress] = useState<AddressValue>(emptyAddress());

  const [supplierSearch, setSupplierSearch] = useState("");
  const [selectedSupplierIds, setSelectedSupplierIds] = useState<string[]>([]);
  const [inlineEdits, setInlineEdits] = useState<Record<string, Partial<Override>>>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  // Per-deal CM overrides keyed by sfAccountId. These apply only to the deal
  // being created, not to the supplier's global default.
  const [dealOverrides, setDealOverrides] = useState<
    Record<string, { channelManagerId?: string; name: string; email: string }>
  >({});

  const { data: catalogData, isLoading: isLoadingCatalog } = useListCatalogProducts({
    query: { queryKey: getListCatalogProductsQueryKey(), staleTime: 60000 },
  });
  const catalogOptions = (catalogData ?? []).map((p) => p.name);

  const { data: suppliersData, isLoading: isLoadingSuppliers } = useQuery<{ results: SfSupplier[] }>({
    queryKey: [`${BASE}/api/salesforce/suppliers`],
    queryFn: async () => {
      const r = await fetch(`${BASE}/api/salesforce/suppliers`, { credentials: "include" });
      if (!r.ok) throw new Error((await r.json())?.error ?? "Failed to load suppliers");
      return r.json();
    },
    enabled: step === 1,
    staleTime: 0,
  });

  const selectedSuppliers = (suppliersData?.results ?? []).filter((s) =>
    selectedSupplierIds.includes(s.id),
  );

  const filteredSuppliers = (suppliersData?.results ?? []).filter((s) =>
    !supplierSearch || s.name.toLowerCase().includes(supplierSearch.toLowerCase()),
  );

  const { data: cmBundle, isLoading: isLoadingCMs, error: cmError } = useQuery<{ results: SupplierBundle[] }>({
    queryKey: [`${BASE}/api/salesforce/suppliers-cm-live`, selectedSupplierIds],
    queryFn: async () => {
      const results = await Promise.all(
        selectedSupplierIds.map(async (sfAccountId) => {
          const r = await fetch(`${BASE}/api/salesforce/suppliers/${sfAccountId}/channel-managers`, { credentials: "include" });
          if (!r.ok) {
            const err = await r.json().catch(() => ({}));
            return { sfAccountId, accountName: sfAccountId, channelManagers: [], hasOverride: false, error: err?.error ?? "Failed" };
          }
          return r.json();
        }),
      );
      return { results };
    },
    enabled: step === 2 && selectedSupplierIds.length > 0,
    staleTime: 0,
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const supplierIds = selectedSupplierIds;
      const r = await fetch(`${BASE}/api/local-deals`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          accountName: form.accountName.trim(),
          opportunityName: form.opportunityName.trim(),
          ownerName: form.ownerName.trim() || undefined,
          ownerEmail: form.ownerEmail.trim() || undefined,
          primaryContactName: form.primaryContactName.trim() || undefined,
          primaryContactEmail: form.primaryContactEmail.trim() || undefined,
          primaryContactPhone: form.primaryContactPhone.trim() || undefined,
          billingAddress: addressToPayload(billingAddress),
          closeDate: form.closeDate.trim() || undefined,
          mrr: form.mrr ? Number(form.mrr) : undefined,
          tcv: form.tcv ? Number(form.tcv) : undefined,
          products: selectedProducts,
          notes: form.notes.trim() || undefined,
          supplierIds,
          supplierOverrides: Object.fromEntries(
            Object.entries(dealOverrides).filter(([sfAccountId]) =>
              supplierIds.includes(sfAccountId),
            ),
          ),
        }),
      });
      if (!r.ok) throw new Error((await r.json())?.error ?? "Failed to create deal");
      return r.json();
    },
    onSuccess: (deal) => {
      queryClient.invalidateQueries({ queryKey: [`${BASE}/api/local-deals`] });
      toast.success("Deal created and submitted for approval");
      setLocation(`/deals/local/${deal.id}`);
    },
    onError: (err: unknown) => toastError(err, "Failed to create deal"),
  });

  function saveDealOverride(sfAccountId: string) {
    const draft = inlineEdits[sfAccountId];
    const name = draft?.name?.trim() ?? "";
    const email = draft?.email?.trim() ?? "";
    if (!name || !email) {
      toast.error("Name and email are required");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Invalid email address");
      return;
    }
    setDealOverrides((prev) => ({ ...prev, [sfAccountId]: { name, email } }));
    setEditingId(null);
    setInlineEdits((prev) => {
      const next = { ...prev };
      delete next[sfAccountId];
      return next;
    });
    toast.success("Override saved for this deal");
  }

  function clearDealOverride(sfAccountId: string) {
    setDealOverrides((prev) => {
      const next = { ...prev };
      delete next[sfAccountId];
      return next;
    });
  }

  function setFormField(k: keyof typeof form, v: string) {
    setForm((prev) => ({ ...prev, [k]: v }));
  }

  function toggleSupplier(id: string) {
    setSelectedSupplierIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  const canProceedStep0 = form.accountName.trim() && form.opportunityName.trim();
  const canProceedStep1 = selectedSupplierIds.length > 0;
  const canProceedStep2 = !isLoadingCMs;

  return (
    <div className="space-y-6 max-w-4xl mx-auto animate-in fade-in duration-500">
      <div className="flex items-center gap-4">
        <Link href="/deals">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">New Deal Registration</h1>
          <p className="text-muted-foreground">Create a deal and submit for internal approval.</p>
        </div>
      </div>

      <StepIndicator current={step} />

      {step === 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Deal Information</CardTitle>
            <CardDescription>Enter the details for this deal.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="accountName">Account / Customer Name <span className="text-destructive">*</span></Label>
                <Input id="accountName" value={form.accountName} onChange={(e) => setFormField("accountName", e.target.value)} placeholder="Acme Corporation" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="opportunityName">Opportunity Name <span className="text-destructive">*</span></Label>
                <Input id="opportunityName" value={form.opportunityName} onChange={(e) => setFormField("opportunityName", e.target.value)} placeholder="Acme – UCaaS Migration 2026" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ownerName">Deal Owner Name</Label>
                <Input id="ownerName" value={form.ownerName} onChange={(e) => setFormField("ownerName", e.target.value)} placeholder="Jane Smith" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ownerEmail">Deal Owner Email</Label>
                <Input id="ownerEmail" type="email" value={form.ownerEmail} onChange={(e) => setFormField("ownerEmail", e.target.value)} placeholder="jane@example.com" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="primaryContactName">Primary Contact Name</Label>
                <Input id="primaryContactName" value={form.primaryContactName} onChange={(e) => setFormField("primaryContactName", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="primaryContactEmail">Primary Contact Email</Label>
                <Input id="primaryContactEmail" type="email" value={form.primaryContactEmail} onChange={(e) => setFormField("primaryContactEmail", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="primaryContactPhone">Primary Contact Phone</Label>
                <Input id="primaryContactPhone" value={form.primaryContactPhone} onChange={(e) => setFormField("primaryContactPhone", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="closeDate">Target Close Date</Label>
                <Input id="closeDate" type="date" value={form.closeDate} onChange={(e) => setFormField("closeDate", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mrr">Estimated MRR ($)</Label>
                <Input id="mrr" type="number" min="0" step="0.01" value={form.mrr} onChange={(e) => setFormField("mrr", e.target.value)} placeholder="0.00" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tcv">Estimated TCV ($)</Label>
                <Input id="tcv" type="number" min="0" step="0.01" value={form.tcv} onChange={(e) => setFormField("tcv", e.target.value)} placeholder="0.00" />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Billing / Customer Address</Label>
                <AddressInput value={billingAddress} onChange={setBillingAddress} idPrefix="ba" labelPrefix="Billing" />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label className="flex items-center gap-1.5">
                  <Package className="h-3.5 w-3.5 text-muted-foreground" /> Products
                </Label>
                <ProductMultiSelect
                  selected={selectedProducts}
                  onChange={setSelectedProducts}
                  options={catalogOptions}
                  isLoading={isLoadingCatalog}
                  isEmpty={!isLoadingCatalog && catalogOptions.length === 0}
                />
                {!isLoadingCatalog && catalogOptions.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    No products in the catalog yet.{" "}
                    <Link href="/settings" className="underline text-primary">
                      Add some in Settings
                    </Link>
                    .
                  </p>
                )}
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="notes">Internal Notes</Label>
                <Textarea id="notes" value={form.notes} onChange={(e) => setFormField("notes", e.target.value)} placeholder="Notes (not sent to suppliers)" className="resize-none" />
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex justify-end">
            <Button onClick={() => setStep(1)} disabled={!canProceedStep0}>
              Next: Select Suppliers <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardFooter>
        </Card>
      )}

      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Select Suppliers</CardTitle>
            <CardDescription>Choose one or more suppliers for this deal. Channel managers will be pulled live from Salesforce.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search suppliers..."
                className="pl-9"
                value={supplierSearch}
                onChange={(e) => setSupplierSearch(e.target.value)}
              />
            </div>

            {isLoadingSuppliers ? (
              <div className="space-y-2">
                {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
              </div>
            ) : !suppliersData?.results?.length ? (
              <div className="text-center py-10 text-muted-foreground text-sm">
                No suppliers found. Ensure Salesforce is configured and accounts exist with <code>Type = &apos;Supplier&apos;</code>.
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                {filteredSuppliers.map((s) => {
                  const selected = selectedSupplierIds.includes(s.id);
                  return (
                    <button
                      key={s.id}
                      className={`w-full text-left p-4 rounded-lg border transition-colors flex items-center justify-between gap-4 ${selected ? "bg-primary/5 border-primary" : "bg-card hover:bg-accent/50"}`}
                      onClick={() => toggleSupplier(s.id)}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${selected ? "bg-primary border-primary" : "border-muted-foreground/40"}`}>
                          {selected && <Check className="h-3 w-3 text-primary-foreground" />}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium truncate">{s.name}</div>
                          <div className="text-xs text-muted-foreground">{s.channelManagers.length} channel manager(s){s.routingBasis ? ` · ${s.routingBasis}` : ""}</div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {selectedSupplierIds.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-2 border-t">
                <span className="text-sm text-muted-foreground">Selected:</span>
                {selectedSuppliers.map((s) => (
                  <Badge key={s.id} variant="secondary" className="gap-1">
                    {s.name}
                    <button onClick={() => toggleSupplier(s.id)} className="ml-1 hover:text-destructive">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(0)}>
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
            <Button onClick={() => setStep(2)} disabled={!canProceedStep1}>
              Next: Review Channel Managers <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardFooter>
        </Card>
      )}

      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle>Review Channel Managers</CardTitle>
            <CardDescription>Confirm or correct the channel manager routing for each supplier. Corrections here apply to this deal only.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {isLoadingCMs ? (
              <div className="space-y-3">
                {selectedSupplierIds.map((id) => <Skeleton key={id} className="h-28 w-full" />)}
              </div>
            ) : (cmBundle?.results ?? []).map((bundle) => {
              const dealOverride = dealOverrides[bundle.sfAccountId];
              return (
              <div key={bundle.sfAccountId} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      {bundle.accountName}
                    </h4>
                    {bundle.routingBasis && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                        <Info className="h-3 w-3" />
                        Routing basis: <span className="font-medium">{bundle.routingBasis}</span>
                      </p>
                    )}
                  </div>
                  {dealOverride ? (
                    <Badge variant="outline" className="text-xs">This deal only</Badge>
                  ) : bundle.hasOverride ? (
                    <Badge variant="outline" className="text-xs">Global override</Badge>
                  ) : null}
                </div>

                {bundle.error ? (
                  <p className="text-sm text-destructive bg-destructive/5 rounded p-2">{bundle.error}</p>
                ) : (
                  <div className="space-y-2">
                    {dealOverride ? (
                      <div className="bg-primary/5 border border-primary/20 rounded p-3 text-sm">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-medium text-primary mb-1">Channel Manager (this deal only)</p>
                            <p>{dealOverride.name} &lt;{dealOverride.email}&gt;</p>
                          </div>
                          <Button size="sm" variant="ghost" onClick={() => clearDealOverride(bundle.sfAccountId)}>
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {bundle.hasOverride && bundle.override && (
                          <div className="bg-muted/40 border rounded p-3 text-sm">
                            <p className="font-medium mb-1">Existing global override</p>
                            <p>{bundle.override.name} &lt;{bundle.override.email}&gt;</p>
                            {bundle.override.title && <p className="text-muted-foreground">{bundle.override.title}</p>}
                          </div>
                        )}
                        {bundle.channelManagers.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No channel managers associated in Salesforce.</p>
                        ) : bundle.channelManagers.map((cm, i) => (
                          <div key={cm.id ?? i} className="flex items-start gap-3 p-3 bg-muted/30 rounded text-sm">
                            <User className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                            <div>
                              <p className="font-medium">{cm.name}</p>
                              <p className="text-muted-foreground">{cm.email}{cm.title ? ` — ${cm.title}` : ""}</p>
                              {cm.supportedStates && <p className="text-xs text-muted-foreground mt-0.5">States: {cm.supportedStates}</p>}
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                )}

                {editingId === bundle.sfAccountId ? (
                  <div className="border-t pt-3 space-y-3">
                    <p className="text-sm font-medium">Override Channel Manager (this deal only)</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Name <span className="text-destructive">*</span></Label>
                        <Input size={1} placeholder="Full name" value={inlineEdits[bundle.sfAccountId]?.name ?? ""} onChange={(e) => setInlineEdits((p) => ({ ...p, [bundle.sfAccountId]: { ...p[bundle.sfAccountId], name: e.target.value } }))} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Email <span className="text-destructive">*</span></Label>
                        <Input size={1} type="email" placeholder="email@example.com" value={inlineEdits[bundle.sfAccountId]?.email ?? ""} onChange={(e) => setInlineEdits((p) => ({ ...p, [bundle.sfAccountId]: { ...p[bundle.sfAccountId], email: e.target.value } }))} />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">This change will only apply to the deal you are about to submit. The supplier&apos;s default channel manager will not be modified.</p>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => saveDealOverride(bundle.sfAccountId)}>
                        Use for this deal
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => {
                      setEditingId(bundle.sfAccountId);
                      setInlineEdits((p) => ({
                        ...p,
                        [bundle.sfAccountId]: dealOverride
                          ? { name: dealOverride.name, email: dealOverride.email }
                          : (p[bundle.sfAccountId] ?? {}),
                      }));
                    }}
                  >
                    <Edit2 className="mr-1.5 h-3.5 w-3.5" />
                    {dealOverride ? "Change CM for this deal" : "Use a different CM for this deal"}
                  </Button>
                )}
              </div>
              );
            })}
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)}>
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
            <Button onClick={() => setStep(3)} disabled={!canProceedStep2}>
              Next: Review & Submit <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardFooter>
        </Card>
      )}

      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle>Review & Submit</CardTitle>
            <CardDescription>Review the deal details before submitting for internal approval.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">Deal Details</h3>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div><dt className="text-muted-foreground">Account</dt><dd className="font-medium">{form.accountName}</dd></div>
                <div><dt className="text-muted-foreground">Opportunity</dt><dd className="font-medium">{form.opportunityName}</dd></div>
                {form.ownerName && <div><dt className="text-muted-foreground">Owner</dt><dd>{form.ownerName}</dd></div>}
                {form.ownerEmail && <div><dt className="text-muted-foreground">Owner Email</dt><dd>{form.ownerEmail}</dd></div>}
                {form.closeDate && <div><dt className="text-muted-foreground">Close Date</dt><dd>{form.closeDate}</dd></div>}
                {form.mrr && <div><dt className="text-muted-foreground">MRR</dt><dd>${Number(form.mrr).toLocaleString()}</dd></div>}
                {form.tcv && <div><dt className="text-muted-foreground">TCV</dt><dd>${Number(form.tcv).toLocaleString()}</dd></div>}
                {selectedProducts.length > 0 && (
                  <div className="col-span-2">
                    <dt className="text-muted-foreground">Products</dt>
                    <dd className="flex flex-wrap gap-1 mt-0.5">
                      {selectedProducts.map((p) => <Badge key={p} variant="secondary" className="text-xs">{p}</Badge>)}
                    </dd>
                  </div>
                )}
                {form.notes && <div className="col-span-2"><dt className="text-muted-foreground">Notes</dt><dd>{form.notes}</dd></div>}
              </dl>
            </div>
            <Separator />
            <div>
              <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">Suppliers ({selectedSupplierIds.length})</h3>
              <ul className="space-y-1">
                {selectedSuppliers.map((s) => (
                  <li key={s.id} className="text-sm flex items-center gap-2">
                    <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                    {s.name}
                    <span className="text-muted-foreground text-xs">({s.channelManagers.length} CM{s.channelManagers.length !== 1 ? "s" : ""})</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-muted/40 rounded-lg p-4 text-sm text-muted-foreground">
              Submitting will create this deal with a <strong>Pending Approval</strong> status. An admin will review and approve or deny.
            </div>
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(2)}>
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
            <Button onClick={() => createMutation.mutate()} disabled={createMutation.isPending}>
              {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Submit for Approval
            </Button>
          </CardFooter>
        </Card>
      )}
    </div>
  );
}
